import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
} from '@mui/material';
import { AddCircleOutline, Edit, Delete } from '@mui/icons-material';
import { userEducationService, EducationEntry, EducationEntryRequest } from '../../services/userEducationService';

interface Props {
  entries: EducationEntry[];
  onEntriesChange: (entries: EducationEntry[]) => void;
}

const MAX_ENTRIES = 5;

interface FormState {
  entryType: 'UNIVERSITY_DEGREE' | 'PRE_UNIVERSITY';
  universityName: string;
  subjectStudied: string;
  collegeName: string;
  courseName: string;
  subjects: string;
  city: string;
  country: string;
  yearOfCompletion: string;
}

const emptyForm = (): FormState => ({
  entryType: 'UNIVERSITY_DEGREE',
  universityName: '', subjectStudied: '', collegeName: '',
  courseName: '', subjects: '', city: '', country: '', yearOfCompletion: '',
});

const EducationSection: React.FC<Props> = ({ entries, onEntriesChange }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm());
    setErrors({});
    setDialogOpen(true);
  };

  const openEdit = (entry: EducationEntry) => {
    setEditingId(entry.id);
    setForm({
      entryType: entry.entryType,
      universityName: entry.universityName ?? '',
      subjectStudied: entry.subjectStudied ?? '',
      collegeName: entry.collegeName ?? '',
      courseName: entry.courseName ?? '',
      subjects: entry.subjects ?? '',
      city: entry.city,
      country: entry.country,
      yearOfCompletion: String(entry.yearOfCompletion),
    });
    setErrors({});
    setDialogOpen(true);
  };

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!form.city.trim()) e.city = 'City is required';
    if (!form.country.trim()) e.country = 'Country is required';
    if (!form.yearOfCompletion) e.yearOfCompletion = 'Year is required';
    if (form.entryType === 'UNIVERSITY_DEGREE' && !form.universityName.trim())
      e.universityName = 'University name is required';
    if (form.entryType === 'PRE_UNIVERSITY' && !form.collegeName.trim())
      e.collegeName = 'College name is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    setError(null);
    try {
      const req: EducationEntryRequest = {
        entryType: form.entryType,
        city: form.city,
        country: form.country,
        yearOfCompletion: parseInt(form.yearOfCompletion),
        ...(form.entryType === 'UNIVERSITY_DEGREE'
          ? { universityName: form.universityName, subjectStudied: form.subjectStudied }
          : { collegeName: form.collegeName, courseName: form.courseName, subjects: form.subjects }),
      };
      if (editingId !== null) {
        const updated = await userEducationService.updateEducationEntry(editingId, req);
        onEntriesChange(entries.map(e => e.id === editingId ? updated : e));
      } else {
        const added = await userEducationService.addEducationEntry(req);
        onEntriesChange([...entries, added]);
      }
      setDialogOpen(false);
    } catch (err: any) {
      setError(err.message || 'Failed to save entry');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    setError(null);
    try {
      await userEducationService.deleteEducationEntry(id);
      onEntriesChange(entries.filter(e => e.id !== id));
    } catch (err: any) {
      setError(err.message || 'Failed to delete entry');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Education ({entries.length}/{MAX_ENTRIES})</Typography>
        {entries.length < MAX_ENTRIES && (
          <Button size="small" startIcon={<AddCircleOutline />} onClick={openAdd}>
            Add Entry
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {entries.length === 0 && (
        <Typography variant="body2" color="text.secondary">No education entries yet.</Typography>
      )}

      {entries.map(entry => (
        <Box key={entry.id} sx={{
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          p: 2, mb: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1,
        }}>
          <Box>
            <Typography variant="body1" fontWeight={500}>
              {entry.entryType === 'UNIVERSITY_DEGREE' ? entry.universityName : entry.collegeName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {entry.entryType === 'UNIVERSITY_DEGREE'
                ? entry.subjectStudied
                : `${entry.courseName}${entry.subjects ? ` · ${entry.subjects}` : ''}`}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {entry.city}, {entry.country} · {entry.yearOfCompletion}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
            <IconButton size="small" onClick={() => openEdit(entry)}><Edit fontSize="small" /></IconButton>
            <IconButton size="small" onClick={() => handleDelete(entry.id)}><Delete fontSize="small" /></IconButton>
          </Box>
        </Box>
      ))}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId !== null ? 'Edit' : 'Add'} Education Entry</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField select fullWidth label="Entry Type" value={form.entryType}
              onChange={e => setForm(f => ({ ...f, entryType: e.target.value as any }))}>
              <MenuItem value="UNIVERSITY_DEGREE">University Degree</MenuItem>
              <MenuItem value="PRE_UNIVERSITY">Pre-University</MenuItem>
            </TextField>

            {form.entryType === 'UNIVERSITY_DEGREE' ? (
              <>
                <TextField fullWidth label="University Name" value={form.universityName}
                  onChange={e => setForm(f => ({ ...f, universityName: e.target.value }))}
                  error={!!errors.universityName} helperText={errors.universityName} />
                <TextField fullWidth label="Subject Studied" value={form.subjectStudied}
                  onChange={e => setForm(f => ({ ...f, subjectStudied: e.target.value }))} />
              </>
            ) : (
              <>
                <TextField fullWidth label="College Name" value={form.collegeName}
                  onChange={e => setForm(f => ({ ...f, collegeName: e.target.value }))}
                  error={!!errors.collegeName} helperText={errors.collegeName} />
                <TextField fullWidth label="Course Name" value={form.courseName}
                  onChange={e => setForm(f => ({ ...f, courseName: e.target.value }))} />
                <TextField fullWidth label="Subjects (optional)" value={form.subjects}
                  onChange={e => setForm(f => ({ ...f, subjects: e.target.value }))} />
              </>
            )}

            <TextField fullWidth label="City" value={form.city}
              onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
              error={!!errors.city} helperText={errors.city} />
            <TextField fullWidth label="Country" value={form.country}
              onChange={e => setForm(f => ({ ...f, country: e.target.value }))}
              error={!!errors.country} helperText={errors.country} />
            <TextField fullWidth label="Year of Completion" type="number" value={form.yearOfCompletion}
              onChange={e => setForm(f => ({ ...f, yearOfCompletion: e.target.value }))}
              error={!!errors.yearOfCompletion} helperText={errors.yearOfCompletion} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EducationSection;
